import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Plane,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../constants/permissions';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TravelRequestsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [travelTypeFilter, setTravelTypeFilter] = useState('all');
  const [deleteModalReq, setDeleteModalReq] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter, travelTypeFilter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/travel-desk/requests?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      if (travelTypeFilter !== 'all') url += `travel_type=${travelTypeFilter}&`;

      const { data } = await axios.get(url, { withCredentials: true });
      setRequests(data.requests || []);
    } catch (err) {
      toast.error('Failed to load travel requests');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequest = async (reqId) => {
    try {
      await axios.delete(`${API_URL}/travel-desk/requests/${reqId}`, { withCredentials: true });
      toast.success('Travel Request soft-deleted successfully');
      setDeleteModalReq(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete travel request');
    }
  };

  const filteredRequests = requests.filter((r) => {
    const q = searchTerm.toLowerCase();
    return (
      (r.request_number || '').toLowerCase().includes(q) ||
      (r.employee_name || '').toLowerCase().includes(q) ||
      (r.purpose || '').toLowerCase().includes(q) ||
      (r.department || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto font-sans">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-blue-950 text-white rounded-2xl p-6 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/20 text-amber-400 rounded-xl border border-amber-500/30">
              <FileText className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight">Travel Requests Master</h1>
              <p className="text-xs text-blue-200">
                View, filter, create, and manage all employee corporate travel requisitions.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {(hasPermission(user, 'travel_desk.new_request.edit') || hasPermission(user, 'travel_desk.new_request.view')) && (
            <button
              onClick={() => navigate('/travel/request/new')}
              className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs rounded-xl shadow-lg flex items-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              <Plus className="w-4 h-4" /> Raise New Travel Request
            </button>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search request #, employee, purpose..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs outline-none focus:border-blue-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold">
            <Filter className="w-3.5 h-3.5" /> Status:
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="pending_approval">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="booked">Booked</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <div className="flex items-center gap-1 text-xs text-slate-600 font-bold ml-2">
            Type:
          </div>
          <select
            value={travelTypeFilter}
            onChange={(e) => setTravelTypeFilter(e.target.value)}
            className="px-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold outline-none"
          >
            <option value="all">All Travel Modes</option>
            <option value="flight">Flight</option>
            <option value="train">Train</option>
            <option value="bus">Bus</option>
            <option value="cab">Cab / Taxi</option>
            <option value="hotel">Hotel</option>
            <option value="multi_city">Multi-City</option>
          </select>

          <button
            onClick={fetchRequests}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-xl text-slate-600 transition-colors"
            title="Refresh List"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Requests Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-xs font-bold text-slate-700">
          <span>All Travel Requests ({filteredRequests.length})</span>
          <span className="text-slate-400">Showing page results</span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-500 font-medium">Loading requests list...</div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-12 text-center space-y-3">
            <Plane className="w-12 h-12 text-slate-300 mx-auto" />
            <p className="text-sm text-slate-500 font-semibold">No travel requests found matching your filters.</p>
            <button
              onClick={() => navigate('/travel/request/new')}
              className="px-4 py-2 bg-blue-600 text-white font-bold text-xs rounded-xl shadow-md"
            >
              Raise Travel Request
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] tracking-wider border-b border-slate-200">
                <tr>
                  <th className="p-3.5">Req #</th>
                  <th className="p-3.5">Employee</th>
                  <th className="p-3.5">Department</th>
                  <th className="p-3.5">Purpose / Category</th>
                  <th className="p-3.5">Route</th>
                  <th className="p-3.5">Est. Budget</th>
                  <th className="p-3.5">Current Approval Step</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {filteredRequests.map((r) => {
                  const isDeletable = ['draft', 'submitted', 'pending_approval'].includes(r.status);
                  const isEditable = ['draft', 'submitted', 'pending_approval', 'returned_for_correction'].includes(r.status);

                  return (
                    <tr key={r._id} className="hover:bg-blue-50/40 transition-colors">
                      <td className="p-3.5 font-mono font-bold text-blue-600">
                        <Link to={`/travel/request/${r._id}`} className="hover:underline">
                          {r.request_number}
                        </Link>
                      </td>
                      <td className="p-3.5 font-bold text-slate-900">{r.employee_name}</td>
                      <td className="p-3.5">{r.department}</td>
                      <td className="p-3.5 capitalize">
                        <div>{r.purpose}</div>
                        <div className="text-[10px] text-slate-500 font-semibold">{r.travel_type} · {r.travel_category || 'Domestic'}</div>
                      </td>
                      <td className="p-3.5">
                        {r.journey_details?.[0] ? (
                          <span>{r.journey_details[0].from_city} &rarr; {r.journey_details[0].to_city}</span>
                        ) : (
                          <span className="text-slate-400 italic">N/A</span>
                        )}
                      </td>
                      <td className="p-3.5 font-mono font-bold text-emerald-700">
                        ₹ {(r.estimated_budget || 0).toLocaleString('en-IN')}
                      </td>
                      <td className="p-3.5 capitalize font-bold text-slate-600">
                        {r.current_approval_step?.replace(/_/g, ' ') || 'Completed'}
                      </td>
                      <td className="p-3.5">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold capitalize ${
                          r.status === 'approved' || r.status === 'booked'
                            ? 'bg-emerald-100 text-emerald-800'
                            : r.status === 'rejected'
                            ? 'bg-rose-100 text-rose-800'
                            : r.status === 'cancelled'
                            ? 'bg-slate-100 text-slate-600'
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="p-3.5 text-right space-x-1 shrink-0">
                        <button
                          onClick={() => navigate(`/travel/request/${r._id}`)}
                          className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg font-bold text-[11px]"
                          title="Inspect Details"
                        >
                          <Eye className="w-3.5 h-3.5 inline mr-1" /> Inspect
                        </button>
                        {isEditable && hasPermission(user, 'travel_desk.edit_request.edit') && (
                          <button
                            onClick={() => navigate(`/travel/request/${r._id}/edit`)}
                            className="p-1.5 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded-lg font-bold text-[11px]"
                            title="Edit Request"
                          >
                            <Edit className="w-3.5 h-3.5 inline mr-1" /> Edit
                          </button>
                        )}
                        {isDeletable && hasPermission(user, 'travel_desk.requests.delete') && (
                          <button
                            onClick={() => setDeleteModalReq(r)}
                            className="p-1.5 bg-rose-50 text-rose-600 hover:bg-rose-100 rounded-lg font-bold text-[11px]"
                            title="Delete Request"
                          >
                            <Trash2 className="w-3.5 h-3.5 inline" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModalReq && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-rose-600 font-bold text-base border-b border-slate-200 pb-3">
              <AlertTriangle className="w-6 h-6" /> Confirm Soft Delete
            </div>
            <p className="text-xs text-slate-600">
              Are you sure you want to delete Travel Request <b className="font-mono text-slate-900">{deleteModalReq.request_number}</b>? The record status will be updated to 'deleted' for audit trail compliance.
            </p>
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button
                onClick={() => setDeleteModalReq(null)}
                className="px-4 py-2 bg-slate-100 font-bold text-xs rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteRequest(deleteModalReq._id)}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs rounded-xl shadow-md"
              >
                Confirm Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TravelRequestsList;
